import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  SUPPORTED_LANGUAGES,
  buildAlternateUrl,
  stripLangFromPath,
  extractLangFromPath,
  DEFAULT_LANGUAGE,
} from '../lib/i18nRouting';

// Map ISO 639-1 → og:locale (IETF BCP 47)
const OG_LOCALE_MAP = {
  fr: 'fr_FR',
  en: 'en_US',
  de: 'de_DE',
  it: 'it_IT',
  es: 'es_ES',
};

const SEO_MARKER = 'data-dronemaps-seo';

/**
 * Composant SEO réutilisable : injecte directement dans document.head le title,
 * meta description, keywords, OpenGraph, Twitter Card et JSON-LD pour la page courante,
 * tout en respectant la langue active.
 *
 * On utilise une manipulation DOM directe (pas Helmet) pour éviter les conflits avec
 * <HreflangTags /> qui manipule aussi le head manuellement, et les bugs de Helmet
 * vis-à-vis des template literals dans <title> / <script>.
 *
 * Props :
 *  - title (string) : titre de la page
 *  - description (string) : meta description (≤ 160 caractères recommandés)
 *  - keywords (string) : meta keywords (séparés par des virgules)
 *  - image (string) : URL absolue de l'image OG (défaut = logo)
 *  - type (string) : og:type ('website' par défaut)
 *  - jsonLd (object|array) : objet(s) Schema.org additionnel(s) à injecter
 *  - noindex (bool) : si vrai, robots = noindex
 *  - rawTitle (bool) : si vrai, ne pas suffixer " | Drone Maps"
 */
export default function SeoMeta({
  title,
  description,
  keywords,
  image = 'https://drone-maps.eu/logo.png',
  type = 'website',
  jsonLd,
  noindex = false,
  rawTitle = false,
}) {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const urlLang = extractLangFromPath(location.pathname);
    const currentLang = urlLang || i18n.language || DEFAULT_LANGUAGE;
    const normalizedLang = (currentLang || DEFAULT_LANGUAGE).split('-')[0];
    const cleanPath = stripLangFromPath(location.pathname);
    const canonicalHref = buildAlternateUrl(cleanPath, normalizedLang);

    const safeTitle = (title && String(title)) || 'Drone Maps';
    const fullTitle = rawTitle ? safeTitle : safeTitle + ' | Drone Maps';

    // 1. document.title
    document.title = fullTitle;

    // 2. Cleanup previously injected SEO tags (also strips static
    //    og:locale:alternate defaults from index.html so we don't
    //    accumulate duplicates on top of the ones we're about to inject).
    document.querySelectorAll(`[${SEO_MARKER}]`).forEach((el) => el.remove());
    document
      .querySelectorAll('meta[property="og:locale:alternate"]')
      .forEach((el) => el.remove());

    // 3. Helper to upsert a meta tag
    const upsertMeta = (selector, attrs) => {
      // Remove pre-existing static tag matching selector to avoid duplicates
      document.querySelectorAll(selector).forEach((el) => {
        if (!el.hasAttribute(SEO_MARKER)) el.remove();
      });
      const meta = document.createElement('meta');
      Object.entries(attrs).forEach(([k, v]) => meta.setAttribute(k, v));
      meta.setAttribute(SEO_MARKER, '1');
      document.head.appendChild(meta);
    };

    if (description) {
      upsertMeta('meta[name="description"]', { name: 'description', content: description });
      upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
      upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    }
    if (keywords) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords });
    }

    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1',
    });

    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalHref });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Drone Maps' });
    upsertMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: OG_LOCALE_MAP[normalizedLang] || 'fr_FR',
    });

    SUPPORTED_LANGUAGES.filter((l) => l !== normalizedLang).forEach((l) => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:locale:alternate');
      m.setAttribute('content', OG_LOCALE_MAP[l]);
      m.setAttribute(SEO_MARKER, '1');
      document.head.appendChild(m);
    });
    // NB: og:locale:alternate uses direct createElement (not upsertMeta)
    // because we need N tags with different `content` values. `upsertMeta`
    // would replace them one by one. To dedupe with the static index.html
    // defaults (which don't carry SEO_MARKER), we removed the matching
    // pre-existing ones just before this loop:
    // done above by cleaning `meta[property="og:locale:alternate"]` w/o marker.

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:url"]', { name: 'twitter:url', content: canonicalHref });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });

    // 4. <html lang>
    document.documentElement.lang = normalizedLang;

    // 5. JSON-LD scripts
    const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    jsonLdArray.forEach((obj) => {
      const s = document.createElement('script');
      s.setAttribute('type', 'application/ld+json');
      s.setAttribute(SEO_MARKER, '1');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      document.querySelectorAll(`[${SEO_MARKER}]`).forEach((el) => el.remove());
    };
  }, [
    title,
    description,
    keywords,
    image,
    type,
    noindex,
    rawTitle,
    location.pathname,
    i18n.language,
    // JSON-LD : stringify for stable dep
    jsonLd ? JSON.stringify(jsonLd) : '',
  ]);

  return null;
}
